import React, { useState, useEffect } from 'react';
import { GetAllProducts } from '../../Services/ProductService';
import { GetAllProductAttributesWithValues } from '../../Services/AttributeService';
import api from '../../config/axios';
import endpoints from '../../config/endpoints';

export default function AttributePricing() {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [attributes, setAttributes] = useState([]);
  const [prices, setPrices] = useState({}); // maps productAttributeValueId -> increasePrice string
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      loadProductAttributes(selectedProductId);
    } else {
      setAttributes([]);
      setPrices({});
    }
  }, [selectedProductId]);

  const loadProducts = async () => {
    try {
      const res = await GetAllProducts({ isCustomProduct: false });
      const customRes = await GetAllProducts({ isCustomProduct: true });
      
      let all = [];
      if (res?.status) all = [...all, ...res.data];
      if (customRes?.status) all = [...all, ...customRes.data];

      // Remove duplicate products if any
      const unique = all.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
      setProducts(unique);
      if (unique.length > 0) {
        setSelectedProductId(unique[0].id);
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const loadProductAttributes = async (productId) => {
    setLoading(true);
    setSuccessMsg('');
    setErrorMsg('');
    try {
      const res = await GetAllProductAttributesWithValues({ productId });
      if (res?.status) {
        setAttributes(res.data || []);
        
        // Populate current prices
        const priceMap = {};
        res.data.forEach(attr => {
          if (attr.values) {
            attr.values.forEach(val => {
              priceMap[val.id] = val.increasePrice || '0';
            });
          }
        });
        setPrices(priceMap);
      } else {
        setErrorMsg('Failed to load attributes for product.');
      }
    } catch (err) {
      console.error(err);
      setErrorMsg('Error fetching product attributes.');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (valueId, newPrice) => {
    setPrices(prev => ({
      ...prev,
      [valueId]: newPrice
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');
    setLoading(true);

    const priceList = Object.keys(prices).map(valueId => ({
      productAttributeValueId: parseInt(valueId),
      increasePrice: prices[valueId] || '0'
    }));

    const payload = {
      productId: parseInt(selectedProductId),
      prices: priceList
    };

    try {
      const response = await api.put(endpoints.Attributes.UpdateAttribute + 'Prices', payload);
      if (response.data?.status) {
        setSuccessMsg('Attribute prices updated successfully!');
        // Refresh values from server
        loadProductAttributes(selectedProductId);
      } else {
        setErrorMsg(response.data?.message || 'Failed to update prices.');
      }
    } catch (err) {
      console.error(err);
      setErrorMsg('Error saving prices to server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard mt-5 container">
      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        <h2>Product Attribute Pricing</h2>
      </div>

      <div className="card shadow-sm p-4 mb-5" style={{ borderRadius: '12px', border: 'none', backgroundColor: '#fff' }}>
        <div className="form-group mb-4">
          <label htmlFor="productSelect" className="form-label" style={{ fontWeight: 'bold', color: '#555' }}>
            Select Product
          </label>
          <select
            id="productSelect"
            className="form-select form-control"
            style={{ padding: '12px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '16px' }}
            value={selectedProductId}
            onChange={(e) => setSelectedProductId(e.target.value)}
          >
            <option value="">-- Choose a Product --</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {successMsg && <div className="alert alert-success">{successMsg}</div>}
        {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

        {loading && <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>}

        {!loading && selectedProductId && attributes.length === 0 && (
          <div className="text-center text-muted py-5">
            This product does not have any attributes mapped. Map attributes first in Products or Filters.
          </div>
        )}

        {!loading && attributes.length > 0 && (
          <form onSubmit={handleSave}>
            <div className="row g-4 mt-2">
              {attributes.map((attr) => (
                <div key={attr.id} className="col-md-6">
                  <div className="card p-3 h-100 shadow-sm" style={{ border: '1px solid #eee', borderRadius: '10px' }}>
                    <h5 className="border-bottom pb-2 mb-3 text-secondary" style={{ fontWeight: 'bold' }}>
                      {attr.name} <small style={{ fontSize: '11px', float: 'right', marginTop: '6px' }}>({attr.controlType})</small>
                    </h5>
                    
                    {attr.values && attr.values.length > 0 ? (
                      <div className="d-flex flex-column gap-3">
                        {attr.values.map((val) => (
                          <div key={val.id} className="d-flex align-items-center justify-content-between">
                            <span style={{ fontWeight: '500', color: '#333' }}>{val.name}</span>
                            <div className="input-group" style={{ width: '180px' }}>
                              <span className="input-group-text bg-light">₹</span>
                              <input
                                type="number"
                                step="any"
                                className="form-control"
                                placeholder="0"
                                value={prices[val.id] || ''}
                                onChange={(e) => handlePriceChange(val.id, e.target.value)}
                                style={{ borderRadius: '0 8px 8px 0' }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted small">No values defined for this attribute.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 text-end">
              <button type="submit" className="add-product-btn px-5 py-3" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                SAVE PRICING SETTINGS
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
